'use client';

import Image from "next/image";
import { WorkCase } from "@/data/types";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

interface HeroProps {
  workCase: WorkCase;
  subtitle?: string;
}

export default function Hero({ workCase, subtitle }: HeroProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const articleType = workCase.articleType || "CASE STUDY"; // Standardwert, falls nicht definiert

  useEffect(() => {
    // Prüfen, ob die Seite direkt geladen wurde
    const isDirectLoad = !sessionStorage.getItem('hasNavigated');
    setIsInitialLoad(isDirectLoad);
    
    // Markiere die Seite als navigiert
    sessionStorage.setItem('hasNavigated', 'true');
  }, [pathname, searchParams]);

  // Bei initialem Load statische Version, sonst animiert
  if (isInitialLoad) {
    return (
      <div data-cy="work-case-hero" className="relative w-full h-[60vh] mb-16 overflow-hidden rounded-2xl">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(16,6,36,0.85)] to-[rgba(50,20,80,0.6)] z-10" />

          {workCase.image && (
            <Image
              src={workCase.image}
              alt={workCase.title}
              fill
              className="object-cover"
              priority
              sizes="100vw"
            />
          )}
        </div>

        <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
          <div className="inline-block bg-[#1E0B32]/70 backdrop-blur-sm border border-[#ff88ff]/30 px-4 py-1.5 rounded-lg mb-6 shadow-glow-sm">
            <span className="text-[#ff88ff] text-sm font-medium tracking-wider">{articleType}</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-white bg-clip-text text-transparent bg-gradient-to-br from-white to-white/80">
            {workCase.title}
          </h1>

          <h2 className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl font-light">
            {subtitle || workCase.description}
          </h2>
        </div>
      </div>
    );
  }

  // Animierte Version für Navigation innerhalb der App
  return (
    <motion.div
      data-cy="work-case-hero"
      className="relative w-full h-[60vh] mb-16 overflow-hidden rounded-2xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5 }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(16,6,36,0.85)] to-[rgba(50,20,80,0.6)] z-10" />

        {workCase.image && (
          <Image
            src={workCase.image}
            alt={workCase.title}
            fill
            className="object-cover"
            priority
            sizes="100vw"
          />
        )}
      </motion.div>

      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center px-6">
        <motion.div
          className="inline-block bg-[#1E0B32]/70 backdrop-blur-sm border border-[#ff88ff]/30 px-4 py-1.5 rounded-lg mb-6 shadow-glow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <span className="text-[#ff88ff] text-sm font-medium tracking-wider">{articleType}</span>
        </motion.div>

        <motion.h1
          className="text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-white bg-clip-text text-transparent bg-gradient-to-br from-white to-white/80"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {workCase.title}
        </motion.h1>

        <motion.h2
          className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl font-light"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          {subtitle || workCase.description}
        </motion.h2>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30"
        animate={{
          y: [0, 10, 0],
          opacity: [0.8, 1, 0.8]
        }}
        transition={{
          repeat: Infinity,
          duration: 2
        }}
      >
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 5V19M12 19L5 12M12 19L19 12"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.div>
    </motion.div>
  );
}